import { memo, useEffect, useRef } from 'react';
import type { TranscriptLine } from '../store';
import { useDemoStore } from '../store';

/**
 * Live transcript pane — visitor + agent turns from the Pipecat client (store).
 *
 * R18 client-audio fix (Part C): memoize individual rows so appending a new turn only
 * re-renders the new row, not the entire growing list. Combined with the level-animation
 * decoupling (useAudioLevels → rAF), this keeps per-update main-thread cost flat as the
 * transcript grows — removing the second source of audio-worklet starvation.
 */
export function Transcript() {
  const lines = useDemoStore((s) => s.transcript);
  const endRef = useRef<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-scroll only when a line is added.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines.length]);

  if (!lines.length) return null;
  return (
    // R27 — inter-turn breathing room (gap-3); the edge-fade on the scroll region is applied by the
    // scroll host (VoiceDemo) so the top/bottom of the transcript dissolve rather than hard-clip.
    <div
      className="flex flex-col gap-3 text-sm"
      aria-live="polite"
      aria-label="Conversation transcript"
    >
      {lines.map((l) => (
        <TranscriptRow key={l.id} line={l} />
      ))}
      <div ref={endRef} />
    </div>
  );
}

/**
 * A single transcript bubble (R27 modern dialogue treatment, design-language §8). Two voices at a
 * glance: Gigi = brass-tinted surface + hairline gradient border (`.bubble-gigi`) + an asymmetric
 * "speech corner" (squared bottom-left), left-aligned; the visitor = a quiet hairline outline,
 * right-aligned with the speech corner mirrored (squared bottom-right). Both sit at `elev-rest`,
 * cap at ~70% width, and enter with the 150ms fade+rise (`.reveal-bubble`, reduced-motion → instant).
 * The in-flight (non-final) Gigi line carries the streaming shimmer (`.bubble-streaming`) — driven by
 * the existing client-side `final` flag, presentation-only (no new server signal).
 *
 * Memoized so it only re-renders when its own line changes (id, text, final, or speaker). While the
 * agent is still speaking a line, only that line's row re-renders; all prior final lines are stable.
 */
const TranscriptRow = memo(function TranscriptRow({ line }: { line: TranscriptLine }) {
  const isVisitor = line.speaker === 'visitor';
  const streaming = !isVisitor && !line.final;
  return (
    <div className={isVisitor ? 'flex justify-end' : 'flex justify-start'}>
      <span
        className={`reveal-bubble inline-block max-w-[70%] px-3.5 py-2 shadow-elev-rest ${
          isVisitor
            ? 'rounded-2xl rounded-br-sm ring-1 ring-line-strong text-ink'
            : `bubble-gigi rounded-2xl rounded-bl-sm text-ink ${streaming ? 'bubble-streaming' : ''}`
        } ${line.final ? '' : 'opacity-70'}`}
      >
        {line.text}
      </span>
    </div>
  );
});
